import { useState, useEffect } from "react";
import AdminLayout from "@/components/AdminLayout";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { reportSecurityDenial } from "@/lib/securityTelemetry";
import {
  Briefcase, Upload, MessageSquare, BarChart3,
  ChevronDown, ChevronUp, Trash2, Ban, CheckCircle2,
  Clock, AlertTriangle, Activity, UserPlus, Plus,
  Settings2, Shield,
} from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";

const EMPLOYEE_ROLES = ["crew", "captain", "engineer", "manager", "office", "hse", "executive_team", "shoreside_leadership", "shoreside_employee"] as const;

const ROLE_DISPLAY_LABELS: Record<string, string> = {
  crew: "Crew",
  captain: "Captain",
  engineer: "Engineer",
  manager: "Manager",
  office: "Office",
  hse: "HSE",
  executive_team: "Executive Team",
  shoreside_leadership: "Shoreside Leadership",
  shoreside_employee: "Shoreside Employee",
};

const FEATURE_LABELS: Record<string, string> = {
  page_dashboard: "Dashboard",
  page_announcements: "Announcements",
  page_crew_roster: "Crew Roster",
  page_crew_personnel: "Crew & Personnel",
  page_safety: "Safety & HSE",
  page_directory: "Org Chart / Directory",
  page_documents: "Documents & SOPs",
  page_questions: "Client Questions",
  page_progress: "Project Updates",
  page_fleet: "Fleet & Vessels",
  page_voyage_ops: "Voyage Operations",
  page_maintenance: "Maintenance & PMS",
  page_onboarding: "Onboarding",
  page_training: "Training & Certs",
  page_contracts: "Contracts",
  page_compliance: "Compliance Calendar",
  page_emergency_contacts: "Emergency Contacts",
  page_vendors: "Vendor Directory",
  page_supply_chain: "Supply Chain",
  page_commercial: "Commercial & Finance",
  page_fleet_intel: "Fleet Intel",
  page_vessel_catalogue: "Vessel Catalogue",
  page_satisfaction: "Client Satisfaction",
  page_reading_analytics: "Reading Analytics",
  page_reports: "Reports & Analytics",
  page_esg: "ESG & Emissions",
  page_lessons_learned: "Lessons Learned",
  page_security_docs: "🔐 Security Docs Clearance",
};

type EmployeePerms = {
  employee_role: string;
  page_dashboard: boolean;
  page_announcements: boolean;
  page_crew_roster: boolean;
  page_crew_personnel: boolean;
  page_safety: boolean;
  page_directory: boolean;
  page_documents: boolean;
  page_questions: boolean;
  page_progress: boolean;
  page_fleet: boolean;
  page_voyage_ops: boolean;
  page_maintenance: boolean;
  page_onboarding: boolean;
  page_training: boolean;
  page_contracts: boolean;
  page_compliance: boolean;
  page_emergency_contacts: boolean;
  page_vendors: boolean;
  page_supply_chain: boolean;
  page_commercial: boolean;
  page_fleet_intel: boolean;
  page_vessel_catalogue: boolean;
  page_satisfaction: boolean;
  page_reading_analytics: boolean;
  page_reports: boolean;
  page_esg: boolean;
  page_lessons_learned: boolean;
  page_security_docs: boolean;
};

const defaultPerms: EmployeePerms = {
  employee_role: "crew",
  page_dashboard: true,
  page_announcements: true,
  page_crew_roster: true,
  page_crew_personnel: true,
  page_safety: true,
  page_directory: true,
  page_documents: true,
  page_questions: true,
  page_progress: true,
  page_fleet: true,
  page_voyage_ops: true,
  page_maintenance: true,
  page_onboarding: true,
  page_training: true,
  page_contracts: true,
  page_compliance: true,
  page_emergency_contacts: true,
  page_vendors: true,
  page_supply_chain: true,
  page_commercial: true,
  page_fleet_intel: true,
  page_vessel_catalogue: true,
  page_satisfaction: true,
  page_reading_analytics: true,
  page_reports: true,
  page_esg: true,
  page_lessons_learned: true,
  page_security_docs: false,
};

const EDGE_BASE = `https://zxdwkagqcqojxcwlsdyc.supabase.co/functions/v1`;

const getAuthHeader = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  const accessToken = session?.access_token;

  if (!accessToken) {
    throw new Error("Please sign in as an admin to manage employees.");
  }

  return {
    Authorization: `Bearer ${accessToken}`,
    "Content-Type": "application/json",
  };
};

type Employee = {
  id: string;
  email: string;
  full_name: string | null;
  company_name: string | null;
  banned: boolean;
  last_sign_in: string | null;
  created_at: string | null;
  docs_uploaded: number;
  questions_answered: number;
  recent_responses: { date: string; preview: string }[];
};

const fetchEmployees = async (): Promise<Employee[]> => {
  const headers = await getAuthHeader();
  const res = await fetch(`${EDGE_BASE}/manage-employees?action=list`, { headers });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error ?? "Failed to fetch employees");
  return data.employees;
};

const toggleBan = async (userId: string, ban: boolean) => {
  const headers = await getAuthHeader();
  const res = await fetch(`${EDGE_BASE}/manage-employees?action=toggle-ban`, {
    method: "POST", headers, body: JSON.stringify({ userId, ban }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error ?? "Failed to update account");
  return data;
};

const deleteEmployee = async (userId: string) => {
  const headers = await getAuthHeader();
  const res = await fetch(`${EDGE_BASE}/manage-employees?action=delete`, {
    method: "POST", headers, body: JSON.stringify({ userId }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error ?? "Failed to delete employee");
  return data;
};

const createEmployee = async ({ email, password, fullName }: { email: string; password: string; fullName: string }) => {
  const headers = await getAuthHeader();
  
  // Try to find existing Velocity Offshore company, or create one
  const { data: companies } = await supabase.from("companies").select("id, name");
  let velocityCompany = (companies ?? []).find(c => c.name.toLowerCase().includes("velocity"));
  
  if (!velocityCompany) {
    // Create it via service client (admin has ALL policy)
    const { data: newCompany, error: companyErr } = await supabase
      .from("companies")
      .insert({ name: "Velocity Offshore" })
      .select("id")
      .single();
    if (companyErr) throw new Error("Failed to create company: " + companyErr.message);
    velocityCompany = { id: newCompany.id, name: "Velocity Offshore" };
  }
  
  const res = await fetch(`${EDGE_BASE}/create-employee`, {
    method: "POST",
    headers,
    body: JSON.stringify({ email, password, fullName, companyId: velocityCompany.id, role: "employee" }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error ?? "Failed to create employee");
  return data;
};

const AdminEmployees = () => {
  const { user, isAdmin, loading: authLoading } = useAuth();
  const qc = useQueryClient();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newName, setNewName] = useState("");
  const [newPassword, setNewPassword] = useState("");

  const canManageEmployees = !!user && isAdmin;

  const { data: employees = [], isLoading, error } = useQuery<Employee[]>({
    queryKey: ["admin-employees"],
    queryFn: fetchEmployees,
    enabled: !authLoading && canManageEmployees,
    retry: false,
  });

  const createMutation = useMutation({
    mutationFn: createEmployee,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-employees"] });
      qc.invalidateQueries({ queryKey: ["admin-profiles"] });
      toast.success("Employee account created successfully");
      setShowForm(false);
      setNewEmail("");
      setNewName("");
      setNewPassword("");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const banMutation = useMutation({
    mutationFn: ({ userId, ban }: { userId: string; ban: boolean }) => toggleBan(userId, ban),
    onSuccess: (_, { ban }) => {
      qc.invalidateQueries({ queryKey: ["admin-employees"] });
      toast.success(ban ? "Employee account disabled" : "Employee account re-enabled");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (userId: string) => deleteEmployee(userId),
    onSuccess: () => {
      setConfirmDelete(null);
      qc.invalidateQueries({ queryKey: ["admin-employees"] });
      qc.invalidateQueries({ queryKey: ["admin-user-count"] });
      toast.success("Employee account deleted");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const totalDocs = employees.reduce((s, e) => s + e.docs_uploaded, 0);
  const totalReplies = employees.reduce((s, e) => s + e.questions_answered, 0);
  const activeCount = employees.filter((e) => !e.banned).length;

  if (authLoading) {
    return (
      <AdminLayout>
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-2 border-teal/30 border-t-teal rounded-full animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  if (!canManageEmployees) {
    return (
      <AdminLayout>
        <div className="glass-card rounded-2xl p-10 text-center">
          <AlertTriangle className="w-10 h-10 text-destructive/50 mx-auto mb-3" />
          <p className="text-primary-foreground/60 font-body text-sm">Please sign in with an admin account to manage employees.</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="text-teal/60 text-[11px] font-bold tracking-[4px] uppercase font-body mb-2">Admin</p>
          <h1 className="font-display text-3xl text-primary-foreground tracking-tight">
            Employee <span className="accent-text">Management</span>
          </h1>
        </div>
        <Button
          onClick={() => setShowForm(!showForm)}
          className="bg-teal hover:bg-teal/80 text-primary font-body font-bold text-xs tracking-wide"
        >
          <UserPlus className="w-4 h-4 mr-2" />
          Add Employee
        </Button>
      </div>

      {/* Create employee form */}
      {showForm && (
        <div className="glass-card rounded-2xl p-6 mb-8 border border-teal/20">
          <p className="text-[11px] font-bold tracking-[3px] uppercase text-teal/60 font-body mb-4">Create Employee Account</p>
          <p className="text-xs text-primary-foreground/40 font-body mb-4">
            Employees must use an <strong>@velocityoffshore.com</strong> email address.
          </p>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!newEmail.endsWith("@velocityoffshore.com")) {
                toast.error("Employee email must end with @velocityoffshore.com");
                return;
              }
              createMutation.mutate({ email: newEmail, password: newPassword, fullName: newName });
            }}
            className="grid grid-cols-1 sm:grid-cols-3 gap-4"
          >
            <Input
              placeholder="Full Name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              required
              className="bg-primary-foreground/5 border-primary-foreground/10 text-primary-foreground placeholder:text-primary-foreground/30 font-body"
            />
            <Input
              type="email"
              placeholder="name@velocityoffshore.com"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              required
              className="bg-primary-foreground/5 border-primary-foreground/10 text-primary-foreground placeholder:text-primary-foreground/30 font-body"
            />
            <Input
              type="password"
              placeholder="Temporary Password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={6}
              className="bg-primary-foreground/5 border-primary-foreground/10 text-primary-foreground placeholder:text-primary-foreground/30 font-body"
            />
            <div className="sm:col-span-3 flex justify-end gap-3">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setShowForm(false)}
                className="text-primary-foreground/40 hover:text-primary-foreground/70 font-body text-xs"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={createMutation.isPending}
                className="bg-lime hover:bg-lime/80 text-primary font-body font-bold text-xs"
              >
                {createMutation.isPending ? "Creating…" : "Create Employee"}
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Summary stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {[
          { label: "Total Employees", value: employees.length, icon: Briefcase, color: "teal" },
          { label: "Active Accounts", value: activeCount, icon: CheckCircle2, color: "lime" },
          { label: "Docs Uploaded", value: totalDocs, icon: Upload, color: "teal" },
          { label: "Questions Answered", value: totalReplies, icon: MessageSquare, color: "lime" },
        ].map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.label} className="glass-card p-5 rounded-2xl">
              <div className={`w-8 h-8 rounded-xl bg-${s.color}/10 flex items-center justify-center mb-3`}>
                <Icon className={`w-4 h-4 text-${s.color}`} />
              </div>
              <p className="text-2xl font-display text-primary-foreground">{s.value}</p>
              <p className="text-xs text-primary-foreground/40 font-body mt-0.5">{s.label}</p>
            </div>
          );
        })}
      </div>

      {/* Employee list */}
      {isLoading ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-2 border-teal/30 border-t-teal rounded-full animate-spin" />
        </div>
      ) : error ? (
        <div className="glass-card rounded-2xl p-10 text-center">
          <AlertTriangle className="w-10 h-10 text-destructive/50 mx-auto mb-3" />
          <p className="text-primary-foreground/40 font-body text-sm">Failed to load employees. Make sure you are logged in as admin.</p>
        </div>
      ) : employees.length === 0 ? (
        <div className="glass-card rounded-2xl p-16 text-center">
          <Briefcase className="w-12 h-12 text-primary-foreground/15 mx-auto mb-4" />
          <p className="text-primary-foreground/40 font-body">No employee accounts yet.</p>
          <p className="text-primary-foreground/25 font-body text-xs mt-1">Create one from the Users page.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {employees.map((emp) => {
            const expanded = expandedId === emp.id;
            const isDeleting = confirmDelete === emp.id;
            const totalActivity = emp.docs_uploaded + emp.questions_answered;

            return (
              <div key={emp.id} className={`glass-card rounded-2xl overflow-hidden transition-all ${emp.banned ? "opacity-60" : ""}`}>
                {/* Row */}
                <div className="flex items-center gap-4 px-6 py-5">
                  {/* Avatar */}
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${emp.banned ? "bg-destructive/10" : "bg-lime/10"}`}>
                    <span className={`text-sm font-bold font-body ${emp.banned ? "text-destructive/60" : "text-lime"}`}>
                      {(emp.full_name || emp.email)[0].toUpperCase()}
                    </span>
                  </div>

                  {/* Identity */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-body font-medium text-primary-foreground/80">
                        {emp.full_name || "—"}
                      </p>
                      {emp.banned && (
                        <span className="px-2 py-0.5 bg-destructive/15 text-destructive text-[10px] font-bold tracking-[1px] uppercase rounded-full font-body">
                          Disabled
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-primary-foreground/30 font-body truncate">{emp.email}</p>
                  </div>

                  {/* Activity pills */}
                  <div className="hidden sm:flex items-center gap-3 flex-shrink-0">
                    <div className="flex items-center gap-1.5 px-3 py-1.5 bg-primary-foreground/5 rounded-xl">
                      <Upload className="w-3 h-3 text-teal/60" />
                      <span className="text-xs text-primary-foreground/50 font-body">{emp.docs_uploaded}</span>
                    </div>
                    <div className="flex items-center gap-1.5 px-3 py-1.5 bg-primary-foreground/5 rounded-xl">
                      <MessageSquare className="w-3 h-3 text-lime/60" />
                      <span className="text-xs text-primary-foreground/50 font-body">{emp.questions_answered}</span>
                    </div>
                    {emp.last_sign_in && (
                      <div className="hidden lg:flex items-center gap-1.5 px-3 py-1.5 bg-primary-foreground/5 rounded-xl">
                        <Clock className="w-3 h-3 text-primary-foreground/25" />
                        <span className="text-xs text-primary-foreground/40 font-body">
                          {new Date(emp.last_sign_in).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => banMutation.mutate({ userId: emp.id, ban: !emp.banned })}
                      disabled={banMutation.isPending}
                      title={emp.banned ? "Re-enable account" : "Disable account"}
                      className={`p-2 rounded-lg transition-all ${
                        emp.banned
                          ? "text-lime/50 hover:text-lime hover:bg-lime/10"
                          : "text-primary-foreground/20 hover:text-yellow-400 hover:bg-yellow-400/10"
                      }`}
                    >
                      {emp.banned ? <CheckCircle2 className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => setConfirmDelete(isDeleting ? null : emp.id)}
                      title="Delete account"
                      className="p-2 rounded-lg text-primary-foreground/20 hover:text-destructive hover:bg-destructive/10 transition-all"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setExpandedId(expanded ? null : emp.id)}
                      className="p-2 rounded-lg text-primary-foreground/20 hover:text-teal hover:bg-teal/5 transition-all"
                    >
                      {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {/* Delete confirmation bar */}
                {isDeleting && (
                  <div className="mx-6 mb-4 flex items-center justify-between gap-4 px-4 py-3 bg-destructive/10 border border-destructive/20 rounded-xl">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-destructive flex-shrink-0" />
                      <p className="text-xs text-destructive font-body">
                        Permanently delete <strong>{emp.full_name || emp.email}</strong>? This cannot be undone.
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={() => setConfirmDelete(null)}
                        className="px-3 py-1.5 text-xs text-primary-foreground/40 hover:text-primary-foreground/70 font-body transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => deleteMutation.mutate(emp.id)}
                        disabled={deleteMutation.isPending}
                        className="px-4 py-1.5 bg-destructive text-white text-xs font-bold font-body rounded-lg hover:bg-destructive/80 transition-all disabled:opacity-50"
                      >
                        {deleteMutation.isPending ? "Deleting…" : "Delete"}
                      </button>
                    </div>
                  </div>
                )}

                {/* Expanded activity log */}
                {expanded && (
                  <div className="px-6 pb-6 border-t border-primary-foreground/5 pt-5">
                    <div className="flex items-center gap-2 mb-4">
                      <Activity className="w-4 h-4 text-teal/60" />
                      <p className="text-[11px] font-bold tracking-[2px] uppercase text-primary-foreground/30 font-body">
                        Activity Log
                      </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
                      <div className="p-4 bg-primary-foreground/3 rounded-xl border border-primary-foreground/5 text-center">
                        <Upload className="w-5 h-5 text-teal/50 mx-auto mb-1" />
                        <p className="text-2xl font-display text-primary-foreground">{emp.docs_uploaded}</p>
                        <p className="text-[11px] text-primary-foreground/30 font-body">Documents uploaded</p>
                      </div>
                      <div className="p-4 bg-primary-foreground/3 rounded-xl border border-primary-foreground/5 text-center">
                        <MessageSquare className="w-5 h-5 text-lime/50 mx-auto mb-1" />
                        <p className="text-2xl font-display text-primary-foreground">{emp.questions_answered}</p>
                        <p className="text-[11px] text-primary-foreground/30 font-body">Questions answered</p>
                      </div>
                      <div className="p-4 bg-primary-foreground/3 rounded-xl border border-primary-foreground/5 text-center">
                        <Clock className="w-5 h-5 text-primary-foreground/20 mx-auto mb-1" />
                        <p className="text-sm font-display text-primary-foreground">
                          {emp.last_sign_in ? new Date(emp.last_sign_in).toLocaleDateString() : "Never"}
                        </p>
                        <p className="text-[11px] text-primary-foreground/30 font-body">Last sign in</p>
                      </div>
                    </div>

                    {emp.recent_responses.length > 0 ? (
                      <div>
                        <p className="text-[11px] font-bold tracking-[2px] uppercase text-primary-foreground/25 font-body mb-3">
                          Recent question replies
                        </p>
                        <div className="space-y-2">
                          {emp.recent_responses.map((r, i) => (
                            <div key={i} className="flex items-start gap-3 p-3 bg-lime/3 border border-lime/10 rounded-xl">
                              <MessageSquare className="w-3.5 h-3.5 text-lime/40 flex-shrink-0 mt-0.5" />
                              <div className="min-w-0">
                                <p className="text-xs text-primary-foreground/60 font-body leading-relaxed">
                                  "{r.preview}{r.preview.length >= 80 ? "…" : ""}"
                                </p>
                                <p className="text-[10px] text-primary-foreground/20 font-body mt-1">
                                  {new Date(r.date).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <p className="text-xs text-primary-foreground/25 font-body text-center py-4">
                        No question replies yet.
                      </p>
                    )}

                    {/* Permissions management */}
                    <EmployeePermissionsPanel userId={emp.id} employeeName={emp.full_name || emp.email} />

                    <div className="mt-4 pt-4 border-t border-primary-foreground/5 flex items-center gap-4 text-[11px] text-primary-foreground/25 font-body">
                      <span>Account created: {emp.created_at ? new Date(emp.created_at).toLocaleDateString() : "—"}</span>
                      {emp.company_name && <span>Company: {emp.company_name}</span>}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </AdminLayout>
  );
};

/* ─── Inline permissions panel for each employee ─── */
const EmployeePermissionsPanel = ({ userId, employeeName }: { userId: string; employeeName: string }) => {
  const qc = useQueryClient();
  const [perms, setPerms] = useState<EmployeePerms>(defaultPerms);
  const [dirty, setDirty] = useState(false);

  // NOTE: security_access_code / security_code_expires_at are intentionally
  // excluded — SELECT on those columns is revoked from the authenticated role
  // so the secret never leaves the server. Admins fetch them via the
  // admin_get_employee_security_code RPC below.
  const SAFE_PERMISSION_COLUMNS =
    "id, user_id, employee_role, military_access, page_announcements, page_commercial, page_compliance, page_contracts, page_crew_personnel, page_crew_roster, page_dashboard, page_directory, page_documents, page_ear_mar, page_emergency_contacts, page_esg, page_fleet, page_fleet_intel, page_lessons_learned, page_maintenance, page_onboarding, page_progress, page_questions, page_reading_analytics, page_reports, page_safety, page_satisfaction, page_security_docs, page_supply_chain, page_training, page_vendors, page_vessel_catalogue, page_voyage_ops, created_at, updated_at";

  const { data: savedPerms, isLoading } = useQuery({
    queryKey: ["employee-perms", userId],
    queryFn: async () => {
      const { data } = await supabase
        .from("employee_permissions")
        .select(SAFE_PERMISSION_COLUMNS)
        .eq("user_id", userId)
        .maybeSingle();
      return data;
    },
  });

  useEffect(() => {
    if (savedPerms) {
      const sp = savedPerms as any;
      const mapped: EmployeePerms = {
        employee_role: sp.employee_role,
        page_dashboard: sp.page_dashboard,
        page_announcements: sp.page_announcements,
        page_crew_roster: sp.page_crew_roster,
        page_crew_personnel: sp.page_crew_personnel ?? true,
        page_safety: sp.page_safety,
        page_directory: sp.page_directory,
        page_documents: sp.page_documents,
        page_questions: sp.page_questions,
        page_progress: sp.page_progress,
        page_fleet: sp.page_fleet,
        page_voyage_ops: sp.page_voyage_ops ?? true,
        page_maintenance: sp.page_maintenance ?? true,
        page_onboarding: sp.page_onboarding,
        page_training: sp.page_training,
        page_contracts: sp.page_contracts,
        page_compliance: sp.page_compliance,
        page_emergency_contacts: sp.page_emergency_contacts,
        page_vendors: sp.page_vendors,
        page_supply_chain: sp.page_supply_chain ?? true,
        page_commercial: sp.page_commercial ?? true,
        page_fleet_intel: sp.page_fleet_intel ?? true,
        page_vessel_catalogue: sp.page_vessel_catalogue ?? true,
        page_satisfaction: sp.page_satisfaction ?? true,
        page_reading_analytics: sp.page_reading_analytics ?? true,
        page_reports: sp.page_reports ?? true,
        page_esg: sp.page_esg ?? true,
        page_lessons_learned: sp.page_lessons_learned ?? true,
        page_security_docs: sp.page_security_docs ?? false,
      };
      setPerms(mapped);
    } else {
      setPerms(defaultPerms);
    }
    setDirty(false);
  }, [savedPerms]);

  // Generate a unique security access code
  const generateSecurityCode = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
    let code = "SEC-";
    for (let i = 0; i < 8; i++) code += chars[Math.floor(Math.random() * chars.length)];
    return code;
  };

  const [securityCode, setSecurityCode] = useState<string | null>(null);
  const [securityCodeExpiry, setSecurityCodeExpiry] = useState<string | null>(null);
  const [showSecurityCode, setShowSecurityCode] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase.rpc("admin_get_employee_security_code", {
        _user_id: userId,
      });
      if (error) void reportSecurityDenial(error, { operation: "rpc:admin_get_employee_security_code", extra: { userId } });
      if (cancelled) return;
      if (error || !data || !Array.isArray(data) || data.length === 0) {
        setSecurityCode(null);
        setSecurityCodeExpiry(null);
        return;
      }
      const row = data[0] as { security_access_code: string | null; security_code_expires_at: string | null };
      setSecurityCode(row.security_access_code ?? null);
      setSecurityCodeExpiry(row.security_code_expires_at ?? null);
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, savedPerms]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      let codeToSave = securityCode;
      let expiryToSave = securityCodeExpiry;
      // If security docs just got enabled and no code exists, generate one with 30-day expiry
      if (perms.page_security_docs && !codeToSave) {
        codeToSave = generateSecurityCode();
        const expiry = new Date();
        expiry.setDate(expiry.getDate() + 30);
        expiryToSave = expiry.toISOString();
        setSecurityCode(codeToSave);
        setSecurityCodeExpiry(expiryToSave);
        setShowSecurityCode(true);
      }
      // If security docs got disabled, clear the code
      if (!perms.page_security_docs) {
        codeToSave = null;
        expiryToSave = null;
        setSecurityCode(null);
        setSecurityCodeExpiry(null);
      }

      const payload = { ...perms, updated_at: new Date().toISOString() };
      if (savedPerms) {
        const { error } = await supabase
          .from("employee_permissions")
          .update(payload)
          .eq("user_id", userId);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("employee_permissions")
          .insert({ user_id: userId, ...payload });
        if (error) throw error;
      }

      // Security access code lives in a separate restricted table; upsert via admin RPC.
      const { error: codeErr } = await supabase.rpc("admin_set_employee_security_code" as any, {
        _user_id: userId,
        _code: codeToSave,
        _expires_at: expiryToSave,
      });
      if (codeErr) {
        void reportSecurityDenial(codeErr, { operation: "rpc:admin_set_employee_security_code", extra: { userId } });
        throw codeErr;
      }

    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["employee-perms", userId] });
      setDirty(false);
      toast.success(`Permissions updated for ${employeeName}`);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const updatePerm = (key: keyof EmployeePerms, value: any) => {
    setPerms((p) => ({ ...p, [key]: value }));
    setDirty(true);
  };

  if (isLoading) {
    return (
      <div className="mt-5 pt-5 border-t border-primary-foreground/5">
        <div className="flex items-center gap-2 mb-4">
          <Settings2 className="w-4 h-4 text-teal/60" />
          <p className="text-[11px] font-bold tracking-[2px] uppercase text-primary-foreground/30 font-body">Role & Access</p>
        </div>
        <div className="w-6 h-6 border-2 border-teal/30 border-t-teal rounded-full animate-spin mx-auto" />
      </div>
    );
  }

  const featureKeys = Object.keys(FEATURE_LABELS) as (keyof typeof FEATURE_LABELS)[];

  return (
    <div className="mt-5 pt-5 border-t border-primary-foreground/5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Settings2 className="w-4 h-4 text-teal/60" />
          <p className="text-[11px] font-bold tracking-[2px] uppercase text-primary-foreground/30 font-body">Role & Access</p>
        </div>
        {dirty && (
          <Button
            size="sm"
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending}
            className="bg-teal hover:bg-teal/80 text-primary font-body font-bold text-[11px] h-7 px-4"
          >
            {saveMutation.isPending ? "Saving…" : "Save Changes"}
          </Button>
        )}
      </div>

      {/* Role selector */}
      <div className="mb-4">
        <p className="text-[11px] text-primary-foreground/40 font-body mb-1.5">Employee Role</p>
        <Select value={perms.employee_role} onValueChange={(v) => updatePerm("employee_role", v)}>
          <SelectTrigger className="w-48 bg-primary-foreground/5 border-primary-foreground/10 text-primary-foreground font-body text-xs h-9">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {EMPLOYEE_ROLES.map((r) => (
              <SelectItem key={r} value={r} className="font-body text-xs">{ROLE_DISPLAY_LABELS[r] || r}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Feature access toggles */}
      <p className="text-[11px] text-primary-foreground/40 font-body mb-3">Intranet Feature Access</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {featureKeys.map((key) => (
          <div key={key} className="flex items-center justify-between gap-2 p-3 bg-primary-foreground/3 rounded-xl border border-primary-foreground/5">
            <span className="text-xs text-primary-foreground/60 font-body">{FEATURE_LABELS[key]}</span>
            <Switch
              checked={perms[key as keyof EmployeePerms] as boolean}
              onCheckedChange={(v) => updatePerm(key as keyof EmployeePerms, v)}
              className="data-[state=checked]:bg-lime"
            />
          </div>
        ))}
      </div>

      {/* Security Access Code Display */}
      {perms.page_security_docs && securityCode && (
        <div className="mt-4 p-4 rounded-xl border border-destructive/20 bg-destructive/5">
          <div className="flex items-center gap-2 mb-2">
            <Shield className="w-4 h-4 text-destructive" />
            <span className="text-xs font-bold text-destructive font-body">Security Access Code</span>
          </div>
           <div className="flex items-center gap-3">
            <code className="text-sm font-mono bg-destructive/10 px-3 py-1.5 rounded-lg text-destructive tracking-widest select-all border border-destructive/20">{securityCode}</code>
            <Button
              size="sm"
              variant="outline"
              className="text-[10px] h-7 border-destructive/20 text-destructive hover:bg-destructive/10"
              onClick={() => {
                navigator.clipboard.writeText(securityCode);
                toast.success("Security code copied to clipboard");
              }}
            >
              Copy
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="text-[10px] h-7 border-destructive/20 text-destructive hover:bg-destructive/10"
              onClick={() => {
                const newCode = generateSecurityCode();
                const expiry = new Date();
                expiry.setDate(expiry.getDate() + 30);
                setSecurityCode(newCode);
                setSecurityCodeExpiry(expiry.toISOString());
                setDirty(true);
                toast.info("New code generated with 30-day expiry — save changes to apply");
              }}
            >
              Regenerate
            </Button>
          </div>
          {securityCodeExpiry && (() => {
            const exp = new Date(securityCodeExpiry);
            const now = new Date();
            const daysLeft = Math.ceil((exp.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
            const isExpired = daysLeft <= 0;
            return (
              <div className={`flex items-center gap-2 mt-2 ${isExpired ? "text-destructive" : daysLeft <= 7 ? "text-amber-500" : "text-muted-foreground"}`}>
                <Clock className="w-3 h-3" />
                <span className="text-[10px] font-body">
                  {isExpired
                    ? `⚠️ Code expired on ${exp.toLocaleDateString()} — please regenerate`
                    : `Expires ${exp.toLocaleDateString()} (${daysLeft} day${daysLeft === 1 ? "" : "s"} remaining)`}
                </span>
              </div>
            );
          })()}
          <p className="text-[10px] text-muted-foreground mt-1 font-body">Provide this code to {employeeName} to grant access to the Security folder. Codes auto-expire after 30 days.</p>
        </div>
      )}

      {/* Show code dialog after first save */}
      <Dialog open={showSecurityCode} onOpenChange={setShowSecurityCode}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Shield className="w-5 h-5 text-destructive" /> Security Code Generated
            </DialogTitle>
            <DialogDescription>
              A unique security access code has been generated for {employeeName}. Please share this code securely with the employee.
            </DialogDescription>
          </DialogHeader>
          <div className="text-center py-4">
            <code className="text-lg font-mono bg-destructive/10 px-6 py-3 rounded-xl text-destructive tracking-[4px] select-all border border-destructive/20">{securityCode}</code>
          </div>
          <DialogFooter>
            <Button
              onClick={() => {
                if (securityCode) navigator.clipboard.writeText(securityCode);
                toast.success("Code copied to clipboard");
                setShowSecurityCode(false);
              }}
              className="bg-destructive hover:bg-destructive/80"
            >
              Copy & Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminEmployees;
